import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore"
import { db } from "@/lib/firebase"

export interface UserVote {
  flashId: string
  timestamp: Date
}

export async function addVoteToFlash(flashId: string, userId: string, weekIndex: number) {
  try {
    // Get user data to check if they're an artist
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      await setDoc(userRef, {
        votes: [{
          flashId,
          timestamp: Timestamp.now(),
          weekIndex
        }]
      }, { merge: true })
    }
    
    const userData = userDoc.exists() ? userDoc.data() : {}
    // Determine if user is an artist for vote weighting and self-vote prevention
    const isArtist = userData.isArtist === true
    const artistId = userData.artistId
    
    // Check if artist is voting for their own flash
    if (isArtist && artistId) {
      const flashRef = doc(db, "flashes", flashId)
      const flashDoc = await getDoc(flashRef)
      
      if (flashDoc.exists()) {
        const flashData = flashDoc.data()
        if (flashData.artistId === artistId) {
          throw new Error("Artists cannot vote for their own flash")
        }
      }
    }
    
    // Add vote(s) to flash document using timedVotes
    const flashRef = doc(db, "flashes", flashId)
    const now = Timestamp.now()
    
    // If artist, add vote twice (double weight)
    const votesToAdd = isArtist ? [
      { userId, votedAt: now },
      { userId, votedAt: now }
    ] : [
      { userId, votedAt: now }
    ]
    
    // Get current timedVotes
    const flashDoc = await getDoc(flashRef)
    const currentTimedVotes = flashDoc.exists() ? (flashDoc.data().timedVotes || []) : []
    
    await updateDoc(flashRef, {
      timedVotes: [...currentTimedVotes, ...votesToAdd]
    })

    // Add vote to user's votes array
    const votes = userData.votes || []
    
    // Add new vote
    const newVote = {
      flashId,
      timestamp: now,
      weekIndex
    }
    
    await updateDoc(userRef, {
      votes: [...votes, newVote]
    })
  } catch (error) {
    console.error("Error adding vote:", error)
    throw error
  }
}

export async function removeVoteFromFlash(flashId: string, userId: string) {
  try {
    // Get user data to check if they're an artist
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    const userData = userDoc.exists() ? userDoc.data() : {}
    
    // Remove vote(s) from flash document using timedVotes
    const flashRef = doc(db, "flashes", flashId)
    const flashDoc = await getDoc(flashRef)
    
    if (flashDoc.exists()) {
      const currentTimedVotes = flashDoc.data().timedVotes || []
      
      // Remove all votes from this user (handles both single and double votes)
      const filteredTimedVotes = currentTimedVotes.filter((vote: any) => vote.userId !== userId)
      
      await updateDoc(flashRef, {
        timedVotes: filteredTimedVotes
      })
    }

    // Remove vote from user's votes array
    if (userDoc.exists()) {
      const votes = userData.votes || []
      
      // Filter out the vote for this flash
      const filteredVotes = votes.filter((vote: any) => vote.flashId !== flashId)
      
      await updateDoc(userRef, {
        votes: filteredVotes
      })
    }
  } catch (error) {
    console.error("Error removing vote:", error)
    throw error
  }
}

export async function getUserVotesForWeek(userId: string, weekIndex: number): Promise<UserVote[]> {
  try {
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const votes = userData.votes || []
      
      // Filter votes for this week
      const weekVotes = votes.filter((vote: any) => vote.weekIndex === weekIndex)
      
      return weekVotes.map((vote: any) => ({
        flashId: vote.flashId,
        timestamp: vote.timestamp instanceof Timestamp ? vote.timestamp.toDate() : new Date(vote.timestamp)
      }))
    }
    
    return []
  } catch (error) {
    console.error("Error getting user votes:", error)
    throw error
  }
}

export async function isFirstVoteEver(userId: string): Promise<boolean> {
  try {
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const votes = userData.votes || []
      
      // Return true if the user has no votes at all
      return votes.length === 0
    }
    
    // If user document doesn't exist, this is definitely their first vote
    return true
  } catch (error) {
    console.error("Error checking if first vote:", error)
    return false
  }
}

// New function to check if this is the user's first vote for the current week
export async function isFirstVoteThisWeek(userId: string, weekIndex: number): Promise<boolean> {
  try {
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const votes = userData.votes || []
      
      // Filter votes for this week
      const weekVotes = votes.filter((vote: any) => vote.weekIndex === weekIndex)
      
      // Return true if the user has no votes for this week
      return weekVotes.length === 0
    }
    
    // If user document doesn't exist, this is definitely their first vote
    return true
  } catch (error) {
    console.error("Error checking if first vote this week:", error)
    return false
  }
} 